// Hook custom per recuperare i dettagli di un'agenzia tramite API.
// Nota: uso axios e TypeScript per tipizzare la risposta e gestire errori e loading.

import { useState, useEffect } from "react";
import axios from "axios";

// Base URL dell'API presa dalle variabili d'ambiente
const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;

// Tipizzazione dei dati dell'agenzia
export interface Agency {
  id: number;
  userId: number;
  name: string;
  lastName: string;
  agencyName: string;
  phone1: string;
  phone2: string;
  mobile: string;
  mobile2: string;
  fax: string;
  vat: string;
  rea: string;
  registeredOffice: string;
  businessRegisterProvince: string;
  pec: string;
  website: string;
  email: string;
  codicefiscale: string;
  ragioneSociale: string;
  socialCapital: string;
  soleProprietorship: number;
  address_details: {
    address: string;
    city_name: string;
    city_cap: number;
    province_name: string;
    sigla: string;
  }
  logoOnPhotos: boolean;
}

/**
 * useGetAgencyDetails
 * @param agencyId - ID dell'agenzia da recuperare
 * @returns { agency, loading, error }
 *
 * Nota per il prossimo dev:
 * - `agency` contiene i dettagli tipizzati come Agency | null
 * - `loading` è true mentre la chiamata è in corso
 * - `error` contiene un messaggio di errore in caso di fallimento
 */
export const useGetAgencyDetails = (agencyId: number) => {
  const [agency, setAgency] = useState<Agency | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Se non ho agencyId, esco subito
    if (!agencyId) return;

    const fetchAgencyDetails = async () => {
      try {
        // Chiamata POST all'endpoint /agency
        const response = await axios.post(`${API_BASE_URL}/agency`, {
          agencyId,
          limit: 10,
          offset: 0,
        });

        // salvo i dati nello state
        setAgency(response.data.data);
      } catch (_err) {
        console.error("Error fetching agency details:", _err);
        setError("Failed to fetch agency details");
      } finally {
        setLoading(false); // fine loading
      }
    };

    fetchAgencyDetails();
  }, [agencyId]); // richiamo quando cambia agencyId

  return { agency, loading, error };
};